import React, { useEffect, useState } from 'react'
import { Breadcrumb } from '../../../../components/ui/breadcrumb'
import { useNavigate } from 'react-router-dom'
import { Label } from '../../../../components/ui/label'
import { Combobox } from '../../../../components/ui/combobox'
import toast, { Toaster } from 'react-hot-toast'
import axios from '../../../../lib/axios'
import useAuth from '../../../../hooks/useAuth'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux'
import { RadioGroup, RadioGroupItem } from '../../../../components/ui/radio-group'
import { DatePicker } from '../../../../components/ui/datepicker'
import { subWeeks } from 'date-fns'
import { Button } from '../../../../components/ui/button'
import { useQuery } from '@tanstack/react-query'
import { formatDateToYYYYMMDD } from '../../../../lib/date'
import { Loader2 } from 'lucide-react'
import OrdersTable from './tables/OrdersTable'
import { getCustomerList } from '../../../../lib/fetch/customer'
import WindowsDatePicker from '../../../../components/ui/windows-datepicker'
import { useTranslation } from 'react-i18next'

const Main = () => {
    const { auth } = useAuth()
    const {t} = useTranslation()
    const navigate = useNavigate()
    const partners = useSelector((state:RootState)=>state.partners);
    const orderdraftstate = useSelector((state:RootState)=>state.orderdraftstate);

    const [selected_startdate, setselected_startdate] = useState(subWeeks(new Date(), 2));
    const [selected_enddate, setselected_enddate] = useState(new Date());
    const [selected_filter, setselected_filter] = useState('C');
    const [selected_orderdraftstate, setselected_orderdraftstate] = useState<string | null>(null);
    const [selected_partner, setselected_partner] = useState<string | null>(null);
    const [selected_customer, setselected_customer] = useState<string | null>(null);
    const [selected_currency, setselected_currency] = useState<string | null>(null);
    const [customers_list, setcustomers_list] = useState<Customer[] | null>();

    const {data:orders, refetch, isFetching} = useQuery<OrderDraft[] | null>({
        queryKey: ['overview_orders'],
        queryFn: async () => {
            try {
                const obj = {
                    token: auth.data?.token,
                    partner: selected_partner !== process.env.REACT_APP_RDM_NUM ? selected_partner : '',
                    relnr: (selected_customer !== "clear" && selected_customer !== process.env.REACT_APP_RDM_NUM) ? selected_customer : '',
                    status: selected_orderdraftstate || '',
                    startdate: formatDateToYYYYMMDD(selected_startdate),
                    enddate: formatDateToYYYYMMDD(selected_enddate),
                    searchby: selected_filter,
                }
                const {data} = await axios.post(`order/draft/search`, obj)
                if(data.errorcode === 0) {
                    if(!data.object.orderdraft){ toast.error(t("order_and_pickup.o_overview_orders.toast_no_found")); throw new Error(data.message) }
                    return data.object.orderdraft
                }
                else { throw new Error() }
            } catch (error) {
                console.log(error);
                return null;
            }
        },
        enabled: false
    })

    useEffect(()=>{
        if (auth.data?.partner && auth.data?.partner.split(",").length === 1) {
            handlePickPartner(auth.data.partner.split(",")[0])
        }
    }, [])

    const handlePickPartner = async (relnr:string) => {
        try {
            if(!relnr){ return; }
            if(selected_partner!==relnr){setselected_customer('clear')}
            setselected_partner(relnr);
            setcustomers_list(null);
          
            const list = await getCustomerList(auth, relnr)
            if(list){ setcustomers_list(list) }
            if(list.length === 1){ setselected_customer(list[0].relnr.toString()) }

        } catch (error) {
          console.log(error)
          toast.error(t("order_and_pickup.o_overview_orders.toast_wrong"))
        }
    }

    const searchOrders = () => {
        setselected_currency(null)
        refetch()
    }

    const currency_options = [
        { value: '', label: t("order_and_pickup.o_overview_orders.option_all") },
        ...Array.from(new Set(orders?.map(o => o.deviescurrencyname).filter(Boolean)))
            .map(c => ({ value: c!, label: c! })),
    ]
    const filtered_orders = orders?.filter(o => !selected_currency || o.deviescurrencyname === selected_currency) ?? null

    let orderdraftstate_list = Array.isArray(orderdraftstate) ? orderdraftstate.map((item)=> ({value:item.code, label:item.description})) : []
    orderdraftstate_list.unshift({value:'',label:t("order_and_pickup.o_overview_orders.option_all")})

    let partnersArr = Array.isArray(partners) ? (partners.map((item) => ({value:item.relnr, label:item.name}))) : []
    partnersArr.length > 0 && partnersArr.unshift({value:parseFloat(process.env.REACT_APP_RDM_NUM!), label:t("components.o_partners_ddl.no_partner")})

    let customersArr = Array.isArray(customers_list) ? customers_list.map((item)=> ({value:item.relnr, label:`${item.name} - ${item.clientcode}`})) : []
    customersArr.length > 0 && customersArr.unshift({value:parseFloat(process.env.REACT_APP_RDM_NUM!), label:t("components.o_partners_ddl.no_customer")})

  return (
    <>
    <Toaster />
    <div className='relative w-full p-4 overflow-auto md:p-8'>

        <Breadcrumb items={[{title:<p onClick={()=>{navigate('/bestellen-en-ophalen')}} className='cursor-pointer'>{t("order_and_pickup.o_overview_orders.link_orders")}</p>}, {title:`${t("order_and_pickup.o_overview_orders.link_overview")}`}]}/>
        <h1 className='text-2xl font-semibold'>{t("order_and_pickup.o_overview_orders.title")}</h1>

        <div className='flex flex-col gap-4 p-4 mt-4 border rounded-sm shadow-sm md:mt-8'>
            <div className='grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4'>
                <div>
                    <Label htmlFor='partner'>{t("order_and_pickup.o_overview_orders.partner")}</Label>
                    <Combobox disabled={partners?.length === 1} id='partner' selectedValue={selected_partner ? selected_partner : undefined} onValueChange={(value) => { handlePickPartner((value.value).toString()) }} options={partnersArr} placeholder={t("order_and_pickup.o_overview_orders.partner_placeholder") || ""} input_placeholder={t("order_and_pickup.o_overview_orders.search_placeholder") || ""}/>
                </div>

                <div>
                    <Label htmlFor='end_customer'>{t("order_and_pickup.o_overview_orders.customer")}</Label>
                    <Combobox disabled={!Array.isArray(customers_list)} selectedValue={selected_customer ? selected_customer : undefined} id='end_customer' onValueChange={(value) => { setselected_customer(value.value.toString()) }} options={customersArr} placeholder={t("order_and_pickup.o_overview_orders.customer_placeholder") || ""} input_placeholder={t("order_and_pickup.o_overview_orders.search_placeholder") || ""}/>
                </div>

                <div>
                    <Label htmlFor='orderdraftstate'>{t("order_and_pickup.o_overview_orders.status")}</Label>
                    <Combobox disabled={!Array.isArray(orderdraftstate)} selectedValue={selected_orderdraftstate ? selected_orderdraftstate : undefined} id='orderdraftstate' onValueChange={(value) => {setselected_orderdraftstate(value.value.toString()) }} options={orderdraftstate_list} placeholder={t("order_and_pickup.o_overview_orders.status_placeholder") || ""} input_placeholder={t("order_and_pickup.o_overview_orders.search_placeholder") || ""}/>
                </div>

                <div className='col-span-full'>
                    <Label htmlFor='filter'>{t("order_and_pickup.o_overview_orders.filter")}</Label>
                    <RadioGroup onValueChange={(value)=>{setselected_filter(value)}} value={selected_filter} className='flex flex-wrap gap-4 mt-1'>
                        <div className='flex gap-2'>
                            <RadioGroupItem value='C' id='r_1'/>
                            <Label htmlFor='r_1'>{t("order_and_pickup.o_overview_orders.date_created_between")}</Label>
                        </div>
                        <div className='flex gap-2'>
                            <RadioGroupItem value='D' id='r_2'/>
                            <Label htmlFor='r_2'>{t("order_and_pickup.o_overview_orders.date_collection_between")}</Label>
                        </div>
                    </RadioGroup>
                </div>

                <div>
                    <Label htmlFor='startdate'>{t("order_and_pickup.o_overview_orders.start_date")}</Label>
                    <WindowsDatePicker value={selected_startdate} onDateChange={(date)=>{date && setselected_startdate(date)}}/>
                </div>

                <div>
                    <Label htmlFor='enddate'>{t("order_and_pickup.o_overview_orders.end_date")}</Label>
                    <WindowsDatePicker value={selected_enddate} onDateChange={(date)=>{date && setselected_enddate(date)}}/>
                </div>
            </div> 

            <Button disabled={isFetching} onClick={()=>{searchOrders()}} className='w-fit'>{
            isFetching ? <Loader2 className='animate-spin'/> :
            t("order_and_pickup.o_overview_orders.button")
            }</Button>
        </div>

        {filtered_orders && <OrdersTable orders={filtered_orders} selected_currency={selected_currency} currency_options={currency_options} onCurrencyChange={setselected_currency}/>}
    </div>
    </>
  )
}

export default Main